import RefreshIcon from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { formatDate } from '../format.ts';
import { palette } from '../theme.ts';
import { useDisplayCurrency } from './CurrencyContext.tsx';
import { DISPLAY_CURRENCIES } from './formats.ts';

export function CurrencyToggle({ labeled = false }: Readonly<{ labeled?: boolean }>): React.JSX.Element {
  const currency = useDisplayCurrency();
  const pgkRate = currency.rates?.PGK;
  const audRate = currency.rates?.AUD;
  const displayRate = typeof pgkRate === 'number' && typeof audRate === 'number' ? audRate / pgkRate : null;

  let indicator = '';
  if (currency.status === 'loading') indicator = 'Updating rate\u2026';
  else if (currency.rateUnavailable) indicator = 'Live AUD rate unavailable \u2014 showing PGK';
  else if (displayRate !== null && currency.fetchedAt !== null) {
    indicator = `1 PGK = ${displayRate.toFixed(2)} AUD \u00b7 as of ${formatDate(currency.fetchedAt)}`;
    if (currency.stale || currency.status === 'error') indicator += ' (offline copy)';
  }

  const group = (
    <ToggleButtonGroup
      exclusive
      size="small"
      aria-label="Display currency"
      value={currency.displayCurrency}
      onChange={(_event, next: string | null) => next && currency.setDisplayCurrency(next)}
      sx={{ '& .MuiToggleButton-root': { minWidth: 72, minHeight: 44, px: 1 } }}
    >
      {DISPLAY_CURRENCIES.map((option) => (
        <ToggleButton key={option.code} value={option.code} aria-label={`Show amounts in ${option.code}`}>
          {option.flag} {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  if (!labeled) return <Tooltip title={currency.isConverting ? indicator : ''}><Box>{group}</Box></Tooltip>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {group}
      {currency.isConverting && indicator && (
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: palette.textSecondary, fontSize: '0.68rem', lineHeight: 1.2, flex: 1 }}>
            {indicator}
          </Typography>
          {currency.rateUnavailable ? (
            <Button size="small" onClick={currency.refresh} sx={{ minWidth: 44, minHeight: 44, p: 0.5 }}>Retry</Button>
          ) : (
            <IconButton size="small" aria-label="Refresh exchange rate" onClick={currency.refresh} sx={{ minWidth: 44, minHeight: 44 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
}
